import { useState } from "react";
import { Pagination } from "../components/Pagination";
import {
  Alert,
  Card,
  EmptyState,
  PageHeader,
  Select,
  Table,
  TableSkeleton,
  Td,
  Th,
} from "../components/ui";
import { formatDateTime, formatRelative } from "../lib/format";
import { useEmailLog, useRetryEmail } from "../lib/queries";
import type { EmailLogStatus } from "../lib/types";

const LIMIT = 50;

/** The statuses the API accepts, and the label each one gets in the filter. */
const STATUS_OPTIONS: { value: EmailLogStatus | "all"; label: string }[] = [
  { value: "all", label: "All messages" },
  { value: "sent", label: "Sent" },
  { value: "pending", label: "Pending / retrying" },
  { value: "failed", label: "Failed" },
];

export function EmailLogPage() {
  const [status, setStatus] = useState<EmailLogStatus | "all">("all");
  const [offset, setOffset] = useState(0);
  const { data, isPending, error } = useEmailLog({
    limit: LIMIT,
    offset,
    ...(status === "all" ? {} : { status }),
  });
  const retry = useRetryEmail();
  const [retryingId, setRetryingId] = useState<string | null>(null);

  return (
    <>
      <PageHeader
        title="Email log"
        description="Everything the platform has queued to send, and what happened to it."
        action={
          <Select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as EmailLogStatus | "all");
              setOffset(0);
            }}
            className="w-full sm:max-w-52"
            aria-label="Filter by status"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        }
      />

      <Card>
        {isPending ? (
          <TableSkeleton columns={5} />
        ) : error ? (
          <div className="p-5">
            <Alert>{error.message}</Alert>
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState icon="mail" title="No messages here" />
        ) : (
          <>
            <Table
              head={
                <tr>
                  <Th>When</Th>
                  <Th>To</Th>
                  <Th>Subject</Th>
                  <Th priority="md">Status</Th>
                  <Th priority="lg">Attempts</Th>
                  <Th className="text-right"> </Th>
                </tr>
              }
            >
              {data.data.map((entry) => (
                <tr key={entry.id} className="hover:bg-raised">
                  <Td title={formatDateTime(entry.createdAt)} className="whitespace-nowrap">
                    {formatRelative(entry.createdAt)}
                  </Td>
                  <Td>
                    <span>{entry.toEmail}</span>
                    <p className="mt-0.5 text-xs text-fg-subtle">{kindLabel(entry.kind)}</p>
                  </Td>
                  <Td>
                    <p className="max-w-md truncate">{entry.subject}</p>
                    {entry.lastError && entry.status === "failed" && (
                      <p
                        className="mt-0.5 max-w-md truncate text-xs text-danger"
                        title={entry.lastError}
                      >
                        {entry.lastError}
                      </p>
                    )}
                  </Td>
                  <Td priority="md">
                    <StatusBadge status={entry.status} />
                  </Td>
                  <Td priority="lg">
                    <span className="text-fg-subtle">{entry.attempts}</span>
                  </Td>
                  <Td className="text-right">
                    {entry.status !== "sent" && (
                      <button
                        type="button"
                        disabled={retryingId === entry.id}
                        onClick={() => {
                          setRetryingId(entry.id);
                          retry
                            .mutateAsync(entry.id)
                            .catch(() => setRetryingId(null))
                            .then(() => setRetryingId(null));
                        }}
                        className="text-sm text-accent hover:underline disabled:opacity-50"
                      >
                        {retryingId === entry.id ? "Queuing…" : "Retry"}
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>

            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onChange={setOffset}
            />
          </>
        )}
      </Card>

      <p className="px-1 text-xs leading-6 text-fg-subtle">
        Failed messages stop after eight tries. Retry starts them again. A message already
        delivered cannot be resent from here.
      </p>
    </>
  );
}

/** What kind meant, in one word an operator can scan. */
function kindLabel(kind: string): string {
  if (kind.startsWith("staff_alert:")) return "staff alert";
  const labels: Record<string, string> = {
    invitation: "invitation",
    low_credit: "low-credit warning",
    credit_exhausted: "credit exhausted",
    credit_termination_scheduled: "deletion date set",
    credit_termination_reminder: "deletion in 3 days",
    credit_terminated: "workloads deleted for credit",
    workloads_deleted_by_operator: "workloads deleted by operator",
    credit_restored: "credit restored",
    quota_approved: "limit raised",
    quota_denied: "limit request denied",
    topup_receipt: "top-up receipt",
    payment_failed: "payment failed",
    suspended_by_operator: "suspension notice",
    suspension_lifted: "suspension lifted",
    password_reset: "password reset",
    custom_domain_live: "custom domain live",
    restore_succeeded: "restore finished",
    restore_failed: "restore failed",
    restore_unknown: "restore outcome unknown",
    domain_registered: "domain receipt",
    domain_transfer_started: "domain transfer receipt",
    domain_renewed: "domain renewed",
    domain_renewal_due_30: "domain renewal due, 30 days",
    domain_renewal_due_7: "domain renewal due, 7 days",
    domain_renewal_due_1: "domain renewal due, tomorrow",
    domain_expired: "domain expired",
  };
  return labels[kind] ?? kind;
}

function StatusBadge({ status }: { status: EmailLogStatus }) {
  const tone = status === "sent" ? "good" : status === "pending" ? "warn" : "danger";
  const label = status === "sent" ? "Sent" : status === "pending" ? "Pending" : "Failed";
  return <span className={badgeClass(tone)}>{label}</span>;
}

function badgeClass(tone: "good" | "warn" | "danger"): string {
  const base = "rounded px-1.5 py-0.5 text-[11px] font-medium";
  const tones = {
    good: "border border-mint/30 bg-mint/10 text-mint",
    warn: "border border-warn/30 bg-warn/10 text-warn",
    danger: "border border-danger/30 bg-danger/10 text-danger",
  } as const;
  return `${base} ${tones[tone]}`;
}